/** Sheet/modal component: a dismissible overlay panel rising from the bottom. */
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { t } from '../../i18n/fr';

export interface SheetProps {
  /** Whether the sheet is currently shown. */
  open: boolean;
  /** Called when the user asks to close the sheet (backdrop, Escape, close button). */
  onClose: () => void;
  /** Accessible title, also shown in the header. */
  title: string;
  children: ReactNode;
}

/**
 * Render a bottom sheet / modal dialog. Closes on Escape or backdrop click,
 * and moves focus to itself while open, per `docs/CONVENTIONS.md` §4
 * (accessible: labels, focus, contrast).
 *
 * @param props - Open state, close handler, title and content.
 * @returns The sheet element, or `null` when closed.
 */
export function Sheet({ open, onClose, title, children }: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div className="ui-sheet__backdrop" onClick={onClose}>
      <div
        ref={panelRef}
        className="ui-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="ui-sheet__header">
          <h2 className="ui-sheet__title">{title}</h2>
          <button type="button" className="ui-sheet__close" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
        <div className="ui-sheet__body">{children}</div>
      </div>
    </div>
  );
}
